import { useEffect } from 'react'
import { log } from '../lib/logger'

// Radix holds a page-wide `pointer-events: none` on <body> for as long as a
// modal layer is mounted, and only lifts it when the *closing* layer's exit
// animation reports `animationend`. WKWebView drops that event if the app is
// suspended mid-animation (app switch, screen lock, an incoming call), which
// strands the lock permanently: the overlay stays on screen at full opacity,
// its close button is inert because Radix already considers the layer closed,
// and every tap anywhere on the page is swallowed. JS keeps running, so the app
// looks alive and is simply unusable until it is force-quit — which is exactly
// what #352 reported after opening a facepile dialog.
//
// Recovery is to hand Presence the event it is still waiting for, so the normal
// unmount path runs and Radix releases the lock itself.

// Only a *modal* layer takes the body lock, so only a modal layer can justify
// one. Add new modal primitives here when they land, or the guard will lift a
// live lock out from under them and let taps through to the page.
const MODAL_SLOTS = [
  'dialog-overlay',
  'dialog-content',
  'alert-dialog-overlay',
  'alert-dialog-content',
  'dropdown-menu-content',
  'dropdown-menu-sub-content',
  'select-content',
]

// Popovers are non-modal by default, and an open one (a NUX tip) must not make a
// stranded lock look justified. One that opts into `modal` (RegionPicker) marks
// its content `data-modal` so it counts. Any popover is still worth finishing.
const MODAL_POPOVER = '[data-slot="popover-content"][data-modal]'
const CLOSEABLE_SLOTS = [...MODAL_SLOTS, 'popover-content']

const CLOSED_LAYERS = CLOSEABLE_SLOTS.map(s => `[data-slot="${s}"][data-state="closed"]`).join(',')
const OPEN_MODAL_LAYERS = [
  ...MODAL_SLOTS.map(s => `[data-slot="${s}"][data-state="open"]`),
  `${MODAL_POPOVER}[data-state="open"]`,
].join(',')

/** Locked out of the page with nothing actually open to justify it. */
function isWedged(): boolean {
  return document.body.style.pointerEvents === 'none' && !document.querySelector(OPEN_MODAL_LAYERS)
}

function finishStalledLayers(): number {
  let finished = 0
  for (const el of document.querySelectorAll<HTMLElement>(CLOSED_LAYERS)) {
    const name = getComputedStyle(el).animationName.split(',')[0].trim()
    if (!name || name === 'none') continue
    el.dispatchEvent(new AnimationEvent('animationend', { animationName: name }))
    finished++
  }
  return finished
}

function recover(via: string) {
  if (!isWedged()) return
  const finished = finishStalledLayers()
  // Radix unmounts on the next commit, so let that land before deciding the
  // nudge did not take. A timer, not requestAnimationFrame: rAF is tied to
  // painting and a WebView that has stopped rendering — the case that strands
  // the lock in the first place — may never run the callback.
  window.setTimeout(() => {
    const stillWedged = isWedged()
    if (stillWedged) document.body.style.pointerEvents = ''
    log('ui', 'released stranded modal input lock', { via, finished, forced: stillWedged })
  }, 50)
}

// The exit animations top out at 250ms, so anything still holding the lock this
// long after the trigger is stalled rather than mid-close.
const SETTLE_MS = 600

/**
 * Un-sticks a modal whose exit animation never reported back, so a dropped
 * `animationend` can't leave the whole app untappable. Mounted once, app-wide.
 */
export function useOverlayStallGuard() {
  useEffect(() => {
    let timer: number | undefined
    const schedule = (via: string) => {
      if (timer !== undefined || !isWedged()) return
      timer = window.setTimeout(() => {
        timer = undefined
        recover(via)
      }, SETTLE_MS)
    }

    // A tap that reaches the document while the page is locked still fires —
    // `pointer-events: none` on <body> only moves the hit target up to <html>.
    // That tap is the user telling us the screen is dead.
    const onPointerDown = () => schedule('tap')
    // Returning from the background is where the event gets dropped, so this is
    // usually the one that fires and the user never sees the stuck state.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') schedule('resume')
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      if (timer !== undefined) window.clearTimeout(timer)
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])
}
